'use client'

import {
  ArrowRight,
  MapPin,
  Users,
  Sparkles,
  CheckCircle,
  ExternalLink,
  TreePine,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/landing/header'
import { Footer } from '@/components/landing/footer'
import { MARKETING_APP_HREF } from '@/components/landing/marketingAppHref'
import Link from 'next/link'

export default function ResourcesPage() {
  return (
    <div className="min-h-screen bg-wag-canvas">
      <Header />
      <main>
        <section className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-pink-500 to-blue-500 text-white">
          <div className="container-mobile py-16 md:py-24">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-sm font-semibold uppercase tracking-wide text-white/85 mb-4">
                Resources
              </p>
              <h1 className="text-4xl lg:text-5xl font-bold font-heading leading-tight mb-6">
                Smarter dog outings
              </h1>
              <p className="text-lg opacity-95 leading-relaxed">
                How Wagamour helps with parks, conditions, and gear—aligned with
                the product: Home, Play, Parks, Picks, and Travel (now testing).
                No separate open-ended Q&amp;A product or booking marketplace.
              </p>
            </div>
          </div>
        </section>

        <section
          id="dog-park-finder"
          className="py-16 lg:py-24 bg-white scroll-mt-24"
        >
          <div className="container-mobile">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 text-purple-600 font-semibold text-sm mb-2">
                  <TreePine className="h-5 w-5" />
                  Parks
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold font-heading text-gray-900 mb-4">
                  Park discovery
                </h2>
                <p className="text-xl text-gray-600">
                  Find options near your area and see what matters for the
                  visit.
                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-purple-600" />
                    The challenge
                  </h4>
                  <p className="text-gray-700">
                    Not every park fits every dog—or every afternoon. Distance,
                    shade, crowds, and ground conditions all matter.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    What Wagamour does in the Parks tab
                  </h4>
                  <p className="text-gray-700 mb-4">
                    Parks surfaces listings near your context, combines
                    conditions with community signals where people contribute,
                    and helps you pick a place that fits the moment—not a
                    generic list of every green space everywhere.
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                    <li>Location or manual area to keep results relevant</li>
                    <li>Weather and comfort context where shown</li>
                    <li>Community updates when users share them</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-8">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    In the app
                  </h4>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                    <li>
                      Park cards with distance and ratings where available
                    </li>
                    <li>Condition and timing cues (e.g. heat, crowds)</li>
                    <li>
                      Paths to learn more or open maps as offered in product
                    </li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href={MARKETING_APP_HREF}>
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:from-purple-700 hover:to-pink-600 text-lg px-8 py-6 font-medium"
                    >
                      <Sparkles className="h-5 w-5 mr-2" />
                      Open app
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link href="/#features">
                    <Button
                      size="lg"
                      variant="outline"
                      className="text-lg px-8 py-6 font-medium border-2"
                    >
                      Product overview
                      <ExternalLink className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="dog-park-conditions"
          className="py-16 lg:py-24 bg-gray-50 scroll-mt-24"
        >
          <div className="container-mobile">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold font-heading text-gray-900 mb-4">
                  Conditions &amp; community
                </h2>
                <p className="text-xl text-gray-600">
                  A clearer picture before you load the car.
                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
                  <h4 className="font-semibold text-gray-800 mb-4">
                    What you might see
                  </h4>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                    <li>
                      Status-style cues when reported (e.g. open/closed,
                      surface)
                    </li>
                    <li>Crowd or busyness hints when available</li>
                    <li>Photos or notes from contributors when provided</li>
                    <li>Weather and time-of-day context for the area</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-600" />
                    Community
                  </h4>
                  <p className="text-gray-700">
                    Dog parents can share quick updates where the product allows
                    it—so the map isn’t frozen in time. Quality depends on who’s
                    contributing in your area.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href={MARKETING_APP_HREF}>
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:from-purple-700 hover:to-pink-600 text-lg px-8 py-6 font-medium"
                    >
                      Open app
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link href="/how-it-works">
                    <Button
                      size="lg"
                      variant="outline"
                      className="text-lg px-8 py-6 font-medium border-2"
                    >
                      How Wagamour works
                      <ExternalLink className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="dog-product-recommendations"
          className="py-16 lg:py-24 bg-white scroll-mt-24"
        >
          <div className="container-mobile">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 text-purple-600 font-semibold text-sm mb-2">
                  <Sparkles className="h-5 w-5" />
                  Picks
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold font-heading text-gray-900 mb-4">
                  Contextual product ideas
                </h2>
                <p className="text-xl text-gray-600">
                  Gear and essentials that fit your dog and the day—not a
                  generic catalog.
                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    How Picks works
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4 mb-4">
                    <li>
                      You add dog details in the app where prompted (breed, age,
                      size, energy, etc.).
                    </li>
                    <li>
                      Pet Comfort Intelligence™ combines that with conditions
                      (weather, time, place) to suggest categories and items.
                    </li>
                    <li>
                      Retailer buttons (e.g. Chewy) may appear; some placements
                      are ads or affiliate links, labeled in the interface.
                    </li>
                  </ol>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-600" />
                    Why it feels relevant
                  </h4>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                    <li>Fewer random SKUs—more “fits this outing” framing</li>
                    <li>
                      Explanations when the product shows why something was
                      surfaced
                    </li>
                    <li>
                      Adjusts with weather and season—not a static wishlist
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
                  <p className="text-sm text-gray-600">
                    Affiliate disclosure: links may earn Wagamour a commission
                    at no extra cost to you. Suggestions are not medical or
                    safety guarantees—always use judgment, especially for health
                    decisions.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href={MARKETING_APP_HREF}>
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:from-purple-700 hover:to-pink-600 text-lg px-8 py-6 font-medium"
                    >
                      Open app
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link href="/#features">
                    <Button
                      size="lg"
                      variant="outline"
                      className="text-lg px-8 py-6 font-medium border-2"
                    >
                      Explore the platform
                      <ExternalLink className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
